import Moment from 'moment-timezone';
import { useParams } from 'react-router-dom';
import QueryString from 'qs';

import { ApplicationStatus, SelfSchedulingLinkStatus } from '../../../../../types';
import { BusinessHourReferenceType } from '../../../../../types/business-hours';
import { defaultBusinessHours } from '../../../../../libraries/business-hours';
import useQueryState from '../../../../../hooks/use-query-state';
import { useApplication, useApplications } from '../../../../../hooks/queries/applications';
import { sortPositions } from '../../../../../libraries/interview-templates';

import type { Account, Application, Candidate, EditableInterviewerTemplate, SelfSchedulingLinkPreview, Stage, StageInterview } from '../../../../../types';
import type { CreateSelfSchedulingLinkPayload } from '../../../../../hooks/queries/self-scheduling-links';
import type { QueryClient } from 'react-query';
import type { SelfSchedulingLink } from './use-new-self-scheduling-link';
import type { TokensPayload } from '../../../../../hooks/queries/tokens';
import type { UserMap } from '../../../../../hooks/queries/users';
import { correctPath } from 'libraries/gem';
import type { InterviewerTemplate } from 'components/library/inputs/InterviewerTemplatesForm';

// Returns true if:
// There is only one possible interview for the stage and it has a default duration and at least one interviewer.
// There is a self-scheduling request email template and a candidate calendar event template.
// Note that confirmation email is not required to skip to review step.
export const canSkipToReviewStep = (selfSchedulingLink: SelfSchedulingLink, stage: Stage): boolean => {
  const eligibleStageInterviews = stage?.stage_interviews?.filter(({ ats_schedulable, deleted }) => !deleted && (ats_schedulable === null || ats_schedulable)) || [];
  return eligibleStageInterviews.length === 1 &&
    Boolean(selfSchedulingLink.scheduling_calendar_email) &&
    Boolean(selfSchedulingLink.stage_interview.id) &&
    Boolean(selfSchedulingLink.stage_interview.interview_template?.duration_minutes) &&
    (selfSchedulingLink.stage_interview.interview_template?.interviewer_templates || []).length > 0 &&
    Boolean(selfSchedulingLink.schedule_template?.self_scheduling_request_email_template) &&
    Boolean(selfSchedulingLink.schedule_template?.candidate_calendar_event_template?.id);
};

export const defaultValuesForStageInterview = async (queryClient: QueryClient, account: Account, application: Application, users: UserMap, stageInterview?: StageInterview) => {
  let interviewerTemplates: InterviewerTemplate[] | undefined;
  let duration;
  if (stageInterview?.interview_template) {
    duration = stageInterview.interview_template.duration_minutes;
    const templates = stageInterview.interview_template.interviewer_templates;
    if (templates) {
      interviewerTemplates = constructInterviewerTemplatesForm(templates);
    }
  } else if (stageInterview?.ats_interviewer_ids && stageInterview.ats_interviewer_ids.length > 0) {
    interviewerTemplates = [
      {
        include_past_interviewers: true,
        interviewer_filters: [
          {
            interviewer_filter_expressions: stageInterview.ats_interviewer_ids.map((interviewer_id) => ({
              filterable_id: interviewer_id,
              filterable_type: 'user',
              negated: false,
            })),
          },
        ],
        optional: false,
      },
    ];
  }
  if (duration === undefined && stageInterview?.ats_duration_minutes && stageInterview.ats_duration_minutes > 0) {
    duration = stageInterview.ats_duration_minutes;
  }

  const interviewerValues = defaultValuesForInterviewer(application, users);

  return {
    duration: duration || 0,
    feedbackFormId: account.ats_type === 'lever' ? stageInterview?.feedback_form_id : undefined,
    interviewTimeWindowEnd: stageInterview?.interview_template?.time_window_end,
    interviewTimeWindowStart: stageInterview?.interview_template?.time_window_start,
    interviewerTemplates,
    liveCodingEnabled: stageInterview?.interview_template?.live_coding_enabled || false,
    positions: stageInterview?.interview_template?.positions,
    candidateFacingName: stageInterview?.interview_template?.candidate_facing_name,
    candidateFacingDetails: stageInterview?.interview_template?.candidate_facing_details,
    ...interviewerValues,
  };
};

export const defaultValuesForInterviewer = (application: Application, users: UserMap, interviewerId?: string) => {
  // If the selected interviewer has working hours set, use those as the
  // business hours. Otherwise, default to the schedule template's business
  // hours.
  const user = users[interviewerId!];
  const businessHours = user?.business_hours || user?.default_business_hours || application.current_stage?.schedule_template?.business_hours || defaultBusinessHours(BusinessHourReferenceType.ScheduleTemplate, user?.default_timezone);

  return {
    businessHours: businessHours.map((businessHour) => ({
      ...businessHour,
      start_time: '',
      end_time: '',
      timezone: businessHour.timezone || user?.default_timezone || Moment.tz.guess(),
    })),
  };
};

// If selfSchedulingLink is passed in, then we're constructing a payload for the
// schedule property (so for types candidate_calendar_description and
// confirmation_email).
export const constructSelfSchedulingTokensPayload = (application: Application, stageInterviewName: string, durationMinutes: number | undefined, candidateFacingName: string | undefined, candidateFacingDetails: string | undefined, users: UserMap): TokensPayload['self_scheduling_link'] => ({
  application: {
    candidate: {
      email: application.candidate.email,
      name: application.candidate.name,
      pronouns: application.candidate.pronouns || undefined,
      pronunciation_url: application.candidate.pronunciation_url || undefined,
      resume_url: application.candidate.resume_url || undefined,
      phone_number: application.candidate.phone_number,
      linkedin_url: application.candidate.linkedin_url,
      website: application.candidate.website,
    },
    coordinator: application.coordinator_id && users[application.coordinator_id] ? {
      email: users[application.coordinator_id].email,
      name: users[application.coordinator_id].name,
      phone_number: users[application.coordinator_id].phone_number,
    } : undefined,
    hiring_manager: application.hiring_manager_id && users[application.hiring_manager_id] ? {
      email: users[application.hiring_manager_id].email,
      name: users[application.hiring_manager_id].name,
      phone_number: users[application.hiring_manager_id].phone_number,
    } : undefined,
    recruiter: application.recruiter_id && users[application.recruiter_id] ? {
      email: users[application.recruiter_id].email,
      name: users[application.recruiter_id].name,
      phone_number: users[application.recruiter_id].phone_number,
    } : undefined,
    sourcer: application.sourcer_id && users[application.sourcer_id] ? {
      email: users[application.sourcer_id].email,
      name: users[application.sourcer_id].name,
      phone_number: users[application.sourcer_id].phone_number,
    } : undefined,
    office: application.office ? {
      name: application.office.name,
    } : undefined,
  },
  stage: {
    name: application.current_stage!.name,
    job: {
      name: application.job.name,
      post_name: application.job.post_name,
    },
  },
  stage_interview: stageInterviewName ? {
    name: stageInterviewName,
  } : undefined,
  interview_template: {
    duration_minutes: durationMinutes || 30,
    candidate_facing_name: candidateFacingName,
    candidate_facing_details: candidateFacingDetails,
  },
});

export const constructScheduleTokensPayload = (application: Application, users: UserMap, selfSchedulingLink: SelfSchedulingLink): TokensPayload['schedule'] => ({
  application: {
    ats_id: application.ats_id,
    candidate: {
      ats_id: application.candidate.ats_id,
      email: application.candidate.email,
      name: application.candidate.name,
      pronouns: application.candidate.pronouns || undefined,
      pronunciation_url: application.candidate.pronunciation_url || undefined,
      resume_url: application.candidate.resume_url || undefined,
      phone_number: application.candidate.phone_number,
      linkedin_url: application.candidate.linkedin_url,
      website: application.candidate.website,
    },
    coordinator: application.coordinator_id && users[application.coordinator_id] ? {
      email: users[application.coordinator_id].email,
      name: users[application.coordinator_id].name,
      phone_number: users[application.coordinator_id].phone_number,
    } : undefined,
    hiring_manager: application.hiring_manager_id && users[application.hiring_manager_id] ? {
      email: users[application.hiring_manager_id].email,
      name: users[application.hiring_manager_id].name,
      phone_number: users[application.hiring_manager_id].phone_number,
    } : undefined,
    recruiter: application.recruiter_id && users[application.recruiter_id] ? {
      email: users[application.recruiter_id].email,
      name: users[application.recruiter_id].name,
      phone_number: users[application.recruiter_id].phone_number,
    } : undefined,
    sourcer: application.sourcer_id && users[application.sourcer_id] ? {
      email: users[application.sourcer_id].email,
      name: users[application.sourcer_id].name,
      phone_number: users[application.sourcer_id].phone_number,
    } : undefined,
    office: application.office ? {
      name: application.office.name,
    } : undefined,
    current_stage: {
      job: {
        name: application.job.name,
        post_name: application.job.post_name,
      },
    },
  },
  stage: {
    name: application.current_stage!.name,
    stage_interviews: application.current_stage!.stage_interviews?.filter((stageInterview) => !stageInterview.inline && !stageInterview.deleted).map((stageInterview) => ({
      name: stageInterview.name,
      position: stageInterview.position,
      interview_template: stageInterview.interview_template ? {
        duration_minutes: stageInterview.interview_template.duration_minutes,
        candidate_facing_name: stageInterview.interview_template.candidate_facing_name,
        candidate_facing_details: stageInterview.interview_template.candidate_facing_details,
      } : undefined,
    })),
  },
  // All business hours should have a timezone by this point.
  timezone: selfSchedulingLink?.schedule_template.business_hours[0].timezone!,
  candidate_event_location: selfSchedulingLink?.schedule_template.video_conferencing_enabled ? undefined : selfSchedulingLink?.schedule_template.candidate_calendar_event_template.location,
  schedule_template: {
    candidate_event_location: selfSchedulingLink.schedule_template.candidate_calendar_event_template.location || undefined,
    video_conferencing_enabled: selfSchedulingLink.schedule_template.video_conferencing_enabled,
    onsite: selfSchedulingLink.schedule_template.onsite,
  },
});

export const constructSchedulePreview = (account: Account, selfSchedulingLink: SelfSchedulingLink, stage: Stage, stageInterview: StageInterview, candidate: Candidate): SelfSchedulingLinkPreview => ({
  account: {
    name: account.name,
    color: account.color,
    logo_url: account.logo_url,
    availability_message: account.availability_message,
    availability_message_in_self_scheduling: account.availability_message_in_self_scheduling,
  },
  // In the bulk scheduling case, arbitrarily pick the first application to use in the preview
  application_id: ('application_id' in selfSchedulingLink ? selfSchedulingLink.application_id : selfSchedulingLink.application_ids[0]),
  application: {
    candidate: {
      name: candidate.name,
      email: candidate.email,
    },
    status: ApplicationStatus.Active,
  },
  interview_template: {
    duration_minutes: selfSchedulingLink.stage_interview.interview_template.duration_minutes,
    candidate_facing_name: selfSchedulingLink.stage_interview.interview_template.candidate_facing_name,
    candidate_facing_details: selfSchedulingLink.stage_interview.interview_template.candidate_facing_details,
    interviewer_templates: (selfSchedulingLink.stage_interview.interview_template.interviewer_templates || []).map((template) => ({
      optional: template.optional,
      include_past_interviewers: template.include_past_interviewers,
      interviewer_filters: template.interviewer_filters.map((filter) => ({
        interviewer_filter_expressions: (filter.interviewer_filter_expressions || []).map((exp) => ({
          negated: exp.negated,
          filterable_id: exp.filterable_id,
          filterable_type: exp.filterable_type,
        })),
      })),
    })),
  },
  schedule_template: {
    business_hours: selfSchedulingLink.schedule_template.business_hours.map((bh) => ({
      day: bh.day,
      start_time: bh.start_time || '00:00',
      end_time: bh.end_time || '24:00',
      // All business hours should have a timezone by this point.
      timezone: bh.timezone!,
    })),
    allowed_times: selfSchedulingLink.schedule_template.allowed_times,
    scheduling_interval_minutes: selfSchedulingLink.schedule_template.scheduling_interval_minutes,
    video_conferencing_enabled: selfSchedulingLink.schedule_template.video_conferencing_enabled,
    zoom_host_filters: selfSchedulingLink.schedule_template.zoom_host_filters,
    self_scheduling_advanced_notice_hours: selfSchedulingLink.schedule_template.self_scheduling_advanced_notice_hours,
    self_scheduling_reschedule_enabled: selfSchedulingLink.schedule_template.self_scheduling_reschedule_enabled,
    self_scheduling_reschedule_notice_hours: selfSchedulingLink.schedule_template.self_scheduling_reschedule_notice_hours,
    self_scheduling_cancel_enabled: selfSchedulingLink.schedule_template.self_scheduling_cancel_enabled,
    self_scheduling_cancel_notice_hours: selfSchedulingLink.schedule_template.self_scheduling_cancel_notice_hours,
  },
  stage: {
    name: stage.name,
  },
  stage_interview: {
    name: stageInterview.name,
  },
  status: SelfSchedulingLinkStatus.Requested,
});

export const constructSelfSchedulingLinkPayload = (selfSchedulingLink: SelfSchedulingLink): CreateSelfSchedulingLinkPayload => {
  const payload: CreateSelfSchedulingLinkPayload = {
    scheduling_calendar_email: selfSchedulingLink.scheduling_calendar_email,
    candidate_scheduling_calendar_email: selfSchedulingLink.candidate_scheduling_calendar_email || selfSchedulingLink.scheduling_calendar_email,
    schedule_template: {
      // We used to make selfSchedulingLink exactly the same as the payload, so
      // we didn't have to have a helper function that converts it, but when we
      // moved to calendar event templates, we didn't want to change all of the
      // payloads yet, so for now, they still accept the flattened version. When
      // we finally do convert it, this can be removed.
      candidate_event_title: selfSchedulingLink.schedule_template.candidate_calendar_event_template.title,
      candidate_event_description: selfSchedulingLink.schedule_template.candidate_calendar_event_template.description,
      candidate_event_location: selfSchedulingLink.schedule_template.candidate_calendar_event_template.location,
      candidate_event_additional_attendees: selfSchedulingLink.schedule_template.candidate_calendar_event_template.additional_attendees,
      candidate_event_additional_optional_attendees: selfSchedulingLink.schedule_template.candidate_calendar_event_template.additional_optional_attendees,
      business_hours: selfSchedulingLink.schedule_template.business_hours.map((bh) => ({
        day: bh.day,
        start_time: bh.start_time || '00:00',
        end_time: bh.end_time || '24:00',
        // All business hours should have a timezone by this point.
        timezone: bh.timezone!,
      })),
      allowed_times: selfSchedulingLink.schedule_template.allowed_times,
      scheduling_interval_minutes: selfSchedulingLink.schedule_template.scheduling_interval_minutes,
      onsite: selfSchedulingLink.schedule_template.onsite,
      mark_interviewer_events_as_private: selfSchedulingLink.schedule_template.mark_interviewer_events_as_private,
      mark_candidate_events_as_private: selfSchedulingLink.schedule_template.mark_candidate_events_as_private,
      video_conferencing_enabled: selfSchedulingLink.schedule_template.video_conferencing_enabled,
      zoom_host_filters: selfSchedulingLink.schedule_template.zoom_host_filters,
      create_hiring_channel: selfSchedulingLink.schedule_template.create_hiring_channel,
      confirmation_email_template: selfSchedulingLink.schedule_template.confirmation_email_template ? {
        id: selfSchedulingLink.schedule_template.confirmation_email_template.id,
        name: selfSchedulingLink.schedule_template.confirmation_email_template.name,
        subject: selfSchedulingLink.schedule_template.confirmation_email_template.subject,
        sender_name: selfSchedulingLink.schedule_template.confirmation_email_template.sender_name,
        sender_email: selfSchedulingLink.schedule_template.confirmation_email_template.sender_email,
        cc_emails: selfSchedulingLink.schedule_template.confirmation_email_template.cc_emails,
        bcc_emails: selfSchedulingLink.schedule_template.confirmation_email_template.bcc_emails,
        body: selfSchedulingLink.schedule_template.confirmation_email_template.body,
        attachments: selfSchedulingLink.schedule_template.confirmation_email_template.attachments,
      } : undefined,
      self_scheduling_advanced_notice_hours: selfSchedulingLink.schedule_template.self_scheduling_advanced_notice_hours,
      self_scheduling_reschedule_enabled: selfSchedulingLink.schedule_template.self_scheduling_reschedule_enabled,
      self_scheduling_reschedule_notice_hours: selfSchedulingLink.schedule_template.self_scheduling_reschedule_notice_hours,
      self_scheduling_cancel_enabled: selfSchedulingLink.schedule_template.self_scheduling_cancel_enabled,
      self_scheduling_cancel_notice_hours: selfSchedulingLink.schedule_template.self_scheduling_cancel_notice_hours,
      self_scheduling_email_follow_up_enabled: Boolean(selfSchedulingLink.schedule_template.self_scheduling_email_follow_up_templates?.[0]),
      self_scheduling_email_follow_up_delay_days: selfSchedulingLink.schedule_template.self_scheduling_email_follow_up_templates?.[0].delay_days || undefined,
      self_scheduling_request_email_template: selfSchedulingLink.schedule_template.self_scheduling_request_email_template ? {
        id: selfSchedulingLink.schedule_template.self_scheduling_request_email_template.id,
        name: selfSchedulingLink.schedule_template.self_scheduling_request_email_template.name,
        subject: selfSchedulingLink.schedule_template.self_scheduling_request_email_template.subject,
        sender_name: selfSchedulingLink.schedule_template.self_scheduling_request_email_template.sender_name,
        sender_email: selfSchedulingLink.schedule_template.self_scheduling_request_email_template.sender_email,
        cc_emails: selfSchedulingLink.schedule_template.self_scheduling_request_email_template.cc_emails,
        bcc_emails: selfSchedulingLink.schedule_template.self_scheduling_request_email_template.bcc_emails,
        body: selfSchedulingLink.schedule_template.self_scheduling_request_email_template.body,
        attachments: selfSchedulingLink.schedule_template.self_scheduling_request_email_template.attachments,
      } : undefined,
      room_filters: selfSchedulingLink.schedule_template.room_filters?.map((filter) => ({
        room_filter_expressions: filter.room_filter_expressions.map((exp) => ({
          negated: exp.negated,
          filterable_id: exp.filterable_id,
          filterable_type: exp.filterable_type,
        })),
      })),
    },
    stage_interview: {
      id: selfSchedulingLink.stage_interview.id,
      feedback_form_id: selfSchedulingLink.stage_interview.feedback_form_id,
      interview_template: {
        name: selfSchedulingLink.stage_interview.interview_template.name,
        duration_minutes: selfSchedulingLink.interview_batch_id ? 30 : selfSchedulingLink.stage_interview.interview_template.duration_minutes,
        live_coding_enabled: selfSchedulingLink.stage_interview.interview_template.live_coding_enabled,
        positions: (selfSchedulingLink.stage_interview.interview_template.positions || []).length > 0 ? sortPositions(selfSchedulingLink.stage_interview.interview_template.positions) : undefined,
        time_window_start: selfSchedulingLink.stage_interview.interview_template.time_window_start,
        time_window_end: selfSchedulingLink.stage_interview.interview_template.time_window_end,
        candidate_facing_name: selfSchedulingLink.stage_interview.interview_template.candidate_facing_name,
        candidate_facing_details: selfSchedulingLink.stage_interview.interview_template.candidate_facing_details,
        interviewer_templates: selfSchedulingLink.interview_batch_id ? [] : selfSchedulingLink.stage_interview.interview_template.interviewer_templates?.map((interviewerTemplate) => ({
          description: interviewerTemplate.description,
          optional: interviewerTemplate.optional,
          include_past_interviewers: interviewerTemplate.include_past_interviewers,
          interviewer_filters: interviewerTemplate.interviewer_filters.map((filter) => ({
            interviewer_filter_expressions: filter.interviewer_filter_expressions.map((exp) => ({
              negated: exp.negated,
              filterable_id: exp.filterable_id,
              filterable_type: exp.filterable_type,
            })),
          })),
        })),
      },
    },
    interview_batch_id: selfSchedulingLink.interview_batch_id,
  };

  if ('application_id' in selfSchedulingLink) {
    payload['application_id'] = selfSchedulingLink['application_id'];
  } else {
    payload['application_ids'] = selfSchedulingLink['application_ids'];
  }

  return payload;
};

export const constructInterviewerTemplatesForm = (interviewerTemplates: SelfSchedulingLink['stage_interview']['interview_template']['interviewer_templates'] | EditableInterviewerTemplate[]): InterviewerTemplate[] | undefined => {
  return (interviewerTemplates || []).map((interviewerTemplate) => ({
    description: interviewerTemplate.description ?? '',
    include_past_interviewers: interviewerTemplate.include_past_interviewers,
    interviewer_filters: (interviewerTemplate.interviewer_filters || []).map(({ interviewer_filter_expressions }) => ({
      interviewer_filter_expressions: (interviewer_filter_expressions || []).map((expression) => ({
        filterable_id: expression.filterable_id,
        filterable_type: expression.filterable_type,
        negated: expression.negated,
      })),
    })),
    optional: false,
  }));
};

export const pathForSelfScheduling = (isBulk: boolean, applicationIds: string[], step: string) => {
  if (isBulk) {
    const query = QueryString.stringify({ application: applicationIds }, { arrayFormat: 'repeat' });
    return correctPath(`/app/candidates/bulk-self-schedule/${step}?${query}`);
  } else {
    const appId = applicationIds[0];
    return correctPath(`/app/candidates/${appId}/self-schedule/${step}`);
  }
};

export const useSelfSchedulingApplications = () => {
  // Read the application ID from params for the single scheduling case, and
  // from the query string for the bulk scheduling case
  const { id: singleApplicationId } = useParams<{ id: string }>();
  const [bulkApplicationIDs] = useQueryState<string[]>('application', []);
  let applicationIDs: string[] = [];
  let arbitraryApplicationId = '';
  const isBulk = !singleApplicationId;

  if (!isBulk) {
    applicationIDs = [singleApplicationId];
    arbitraryApplicationId = singleApplicationId;
  } else {
    applicationIDs = bulkApplicationIDs;
    arbitraryApplicationId = bulkApplicationIDs[0];
  }
  // TODO @benhiller: Increase to 50 once timeouts are fixed
  const { data, isLoading: allAppsAreLoading } = useApplications({ id: applicationIDs, limit: 35 });
  // This is sort of redundant with the useApplications call, but
  // useApplication fetches additional data which useApplications does not. It
  // would be inefficient to load all of the data for each application, but the
  // self-scheduling flow does need some of this data, so instead just load it
  // for an arbitrary single application.
  const {
    data: arbitraryApplication,
    isLoading,
  } = useApplication(arbitraryApplicationId);

  return {
    applicationIDs,
    applications: data?.applications,
    arbitraryApplication,
    isBulk,
    isLoading: allAppsAreLoading || isLoading,
  };
};
